import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { isFunction } from 'lodash';
import React, { useEffect, useState } from 'react';

import { Styles } from './includes/styles';

interface DateRangeProps {
  defaultEndDate?: null | string;
  defaultStartDate?: null | string;
  isClearable?: boolean;
  isRequired?: boolean;
  maximumDate?: null | string;
  minimumDate?: null | string;
  onChange?: (range: { endDate: null | string; startDate: null | string }) => void;
}

/**
 * DateRange
 *
 * Displays a date range input component.
 */
export function DateRange({
  defaultEndDate = null,
  defaultStartDate = null,
  isClearable = true,
  isRequired = false,
  maximumDate = null,
  minimumDate = null,
  onChange = () => {},
}: DateRangeProps) {
  const [startDate, setStartDate] = useState<null | string>(defaultStartDate);
  const [endDate, setEndDate] = useState<null | string>(defaultEndDate);

  useEffect(() => {
    if (isFunction(onChange)) {
      onChange({ endDate, startDate });
    }
  }, [startDate, endDate]);

  return (
    <ErrorHandler location="src/shared/ui/components/DateRange/DateRange.tsx">
      <Styles>
        <Layout.Group
          alignItems="bottom"
          alignLayout="left"
          grow
          space={Number(themeConfiguration?.theme?.space?.md) || 16}
        >
          <DateInput
            excludeDate={(date: Date) => {
              // Exclude dates after the end date
              if (endDate) {
                return date.getTime() > new Date(endDate).getTime();
              }
              return false;
            }}
            isClearable={isClearable}
            label="Date Range"
            maximumDate={maximumDate}
            minimumDate={minimumDate}
            model="dateRange.start"
            onChange={(date: string = '') => {
              setStartDate(date);
            }}
            onClear={() => {
              setStartDate(null);
            }}
            subText=""
            validators={{ required: isRequired }}
            value={startDate}
          />
          <DateInput
            excludeDate={(date: Date) => {
              // Exclude dates before the start date
              if (startDate) {
                return date.getTime() < new Date(startDate).getTime();
              }
              return false;
            }}
            hideLabel
            isClearable={isClearable}
            label=""
            maximumDate={maximumDate}
            minimumDate={minimumDate}
            model="dateRange.end"
            onChange={(date: string = '') => {
              setEndDate(date);
            }}
            onClear={() => {
              setEndDate(null);
            }}
            subText=""
            validators={{ required: true }}
            value={endDate}
          />
        </Layout.Group>
      </Styles>
    </ErrorHandler>
  );
}
